import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';

/**
 * MultiSelectEmployee — Reusable multi-select employee picker
 * Supports: checkboxes, tags/chips, Select All, individual removal, search
 * Usage: Projects, Tasks, CRM, Incentives, Approvals
 *
 * Props:
 *   employees       — array of { id, fullName, department, role, employeeCode }
 *   selectedIds     — array of selected employee IDs
 *   onChange        — (selectedEmployees: Employee[]) => void
 *   placeholder     — string
 *   loading         — boolean
 *   disabled        — boolean
 *   maxHeight       — string (default '280px')
 */
const MultiSelectEmployee = ({
  employees = [],
  selectedIds = [],
  onChange,
  placeholder = 'Select employees...',
  loading = false,
  disabled = false,
  maxHeight = '280px',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (open && searchRef?.current) {
      setTimeout(() => searchRef?.current?.focus(), 50);
    }
  }, [open]);

  const filtered = employees?.filter(e => {
    const q = search?.toLowerCase();
    return ((e?.fullName || '')?.toLowerCase()?.includes(q) ||
    (e?.department || '')?.toLowerCase()?.includes(q) ||
    (e?.role || '')?.toLowerCase()?.includes(q) || (e?.employeeCode || '')?.toLowerCase()?.includes(q));
  });

  const selectedEmployees = employees?.filter(e => selectedIds?.includes(e?.id));
  const allFilteredSelected = filtered?.length > 0 && filtered?.every(e => selectedIds?.includes(e?.id));
  const someFilteredSelected = filtered?.some(e => selectedIds?.includes(e?.id));

  const toggleEmployee = (emp) => {
    const isSelected = selectedIds?.includes(emp?.id);
    let next;
    if (isSelected) {
      next = selectedEmployees?.filter(e => e?.id !== emp?.id);
    } else {
      next = [...selectedEmployees, emp];
    }
    onChange?.(next);
  };

  const removeEmployee = (empId, e) => {
    e?.stopPropagation();
    let next = selectedEmployees?.filter(emp => emp?.id !== empId);
    onChange?.(next);
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      // Deselect all filtered
      const filteredIds = new Set(filtered.map(e => e?.id));
      let next = selectedEmployees?.filter(e => !filteredIds?.has(e?.id));
      onChange?.(next);
    } else {
      // Select all filtered (merge with existing)
      const existingIds = new Set(selectedIds);
      const toAdd = filtered?.filter(e => !existingIds?.has(e?.id));
      onChange?.([...selectedEmployees, ...toAdd]);
    }
  };

  const clearAll = (e) => {
    e?.stopPropagation();
    onChange?.([]);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger / Tags display */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={(e) => { if (e?.key === 'Enter' || e?.key === ' ') { e?.preventDefault(); !disabled && setOpen(o => !o); } }}
        className={`
          min-h-[42px] w-full flex flex-wrap items-center gap-1.5 px-3 py-2
          bg-background border rounded-xl text-sm transition-colors cursor-pointer
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50'}
          ${open ? 'border-primary ring-2 ring-primary/20' : 'border-border'}
        `}
      >
        {selectedEmployees?.length === 0 ? (
          <span className="text-muted-foreground flex-1">{loading ? 'Loading employees...' : placeholder}</span>
        ) : (
          <>
            {selectedEmployees?.map(emp => (
              <span
                key={emp?.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary border border-primary/20 rounded-lg text-xs font-medium max-w-[180px]"
              >
                <span className="truncate">{emp?.fullName}</span>
                <button
                  type="button"
                  onClick={(e) => removeEmployee(emp?.id, e)}
                  className="flex-shrink-0 hover:text-error transition-colors ml-0.5"
                  aria-label={`Remove ${emp?.fullName}`}
                >
                  <Icon name="X" size={10} />
                </button>
              </span>
            ))}
          </>
        )}
        <div className="ml-auto flex items-center gap-1 flex-shrink-0">
          {selectedEmployees?.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="p-0.5 hover:text-error text-muted-foreground transition-colors"
              aria-label="Clear all"
            >
              <Icon name="X" size={14} />
            </button>
          )}
          <Icon
            name="ChevronDown"
            size={14}
            className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </div>
      {/* Dropdown */}
      {open && (
        <div className="absolute z-[200] top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                placeholder="Search by name, department, role..."
                className="w-full pl-8 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>

          {/* Select All */}
          {filtered?.length > 0 && (
            <div
              className="flex items-center gap-3 px-3 py-2.5 border-b border-border hover:bg-muted/40 cursor-pointer"
              onClick={toggleSelectAll}
            >
              <div className={`
                w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
                ${allFilteredSelected
                  ? 'bg-primary border-primary'
                  : someFilteredSelected
                    ? 'bg-primary/30 border-primary' :'border-border bg-background'
                }
              `}>
                {allFilteredSelected && <Icon name="Check" size={10} color="white" />}
                {!allFilteredSelected && someFilteredSelected && (
                  <div className="w-2 h-0.5 bg-primary rounded" />
                )}
              </div>
              <span className="text-sm font-semibold text-foreground">
                {allFilteredSelected ? 'Deselect All' : 'Select All'}
                {search && ` (${filtered?.length} matching)`}
              </span>
              {selectedEmployees?.length > 0 && (
                <span className="ml-auto text-xs text-muted-foreground bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  {selectedEmployees?.length} selected
                </span>
              )}
            </div>
          )}

          {/* Employee list */}
          <div className="overflow-y-auto" style={{ maxHeight }}>
            {loading ? (
              <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                Loading employees...
              </div>
            ) : filtered?.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {search ? `No employees match "${search}"` : 'No employees available'}
              </div>
            ) : (
              filtered?.map(emp => {
                const isSelected = selectedIds?.includes(emp?.id);
                return (
                  <div
                    key={emp?.id}
                    onClick={() => toggleEmployee(emp)}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors
                      ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/40'}
                    `}
                  >
                    {/* Checkbox */}
                    <div className={`
                      w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
                      ${isSelected ? 'bg-primary border-primary' : 'border-border bg-background'}
                    `}>
                      {isSelected && <Icon name="Check" size={10} color="white" />}
                    </div>

                    {/* Employee info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                          {emp?.fullName}
                        </span>
                        {emp?.employeeCode && (
                          <span className="text-xs text-muted-foreground font-mono flex-shrink-0">
                            {emp?.employeeCode}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {emp?.department}{emp?.role ? ` · ${emp?.role}` : ''}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {selectedEmployees?.length > 0 && (
            <div className="px-3 py-2 border-t border-border bg-muted/20 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {selectedEmployees?.length} employee{selectedEmployees?.length !== 1 ? 's' : ''} selected
              </span>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-error hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectEmployee;
